import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { ShieldCheck, Shield, KeyRound, CalendarDays, Link2, Unplug } from 'lucide-react'
import { Page, PageHeader, Card, btnPrimary, btnGhost, btnSecondary, StatusPill } from '@/components/rm'
import { Field, Input } from '@/components/rm/form'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/use-auth'
import { enrollTotp, verifyCode, disableFactor, listTotpFactors } from '@/lib/mfa'
import { ThemeModeSelect } from '@/components/theme-provider'
import { phFormat } from '@/lib/ph'
import { LanguageMenu, useT } from '@/i18n'

export const Route = createFileRoute('/_app/account')({ component: AccountPage })

type Enroll = { factorId: string; qrSvg: string; secret: string }

async function issueCodes(): Promise<string[]> {
  const { data, error } = await supabase.rpc('issue_recovery_codes', { p_count: 10 })
  if (error) throw error
  return (data as string[]) ?? []
}
const logEvent = (type: string, meta: Record<string, unknown> = {}) =>
  supabase.rpc('log_security_event', { p_type: type, p_meta: meta })

function AccountPage() {
  // Change password. Migrated users were created with a random placeholder hash — they have no
  // password they could possibly know, and until now the web offered no way to set one (both
  // mobile apps do). That left a recovery link as the only route in, and no way out of it.
  const [pw, setPw] = useState('')
  const [pw2, setPw2] = useState('')
  const [pwBusy, setPwBusy] = useState(false)

  const changePassword = async () => {
    if (pw.length < 8) return toast.error('Password must be at least 8 characters.')
    if (pw !== pw2) return toast.error('Passwords don\u2019t match.')
    setPwBusy(true)
    try {
      const { error } = await supabase.auth.updateUser({ password: pw })
      if (error) throw error
      setPw(''); setPw2('')
      toast.success('Password updated. Use it next time you sign in \u2014 including in the mobile app.')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not update your password.')
    } finally { setPwBusy(false) }
  }

  const { aal, refreshAal } = useAuth()
  const [factorId, setFactorId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [enroll, setEnroll] = useState<Enroll | null>(null)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null)

  const reload = async () => {
    setLoading(true)
    try {
      const factors = await listTotpFactors()
      setFactorId(factors.find((f) => f.status === 'verified')?.id ?? null)
    } finally { setLoading(false) }
  }
  useEffect(() => { void reload() }, [])

  const startEnroll = async () => {
    setError(''); setBusy(true)
    try {
      const e = await enrollTotp('Authenticator app')
      setEnroll({ factorId: e.factorId, qrSvg: e.qrSvg, secret: e.secret })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start setup. Two-factor may not be enabled on the project yet.')
    } finally { setBusy(false) }
  }

  const confirmEnroll = async () => {
    if (!enroll) return
    setError(''); setBusy(true)
    try {
      await verifyCode(enroll.factorId, code)   // reaches aal2
      await refreshAal()
      const codes = await issueCodes()          // one-time backup codes
      await logEvent('mfa_enabled')
      setRecoveryCodes(codes)                   // show them before finishing
      setEnroll(null); setCode('')
      await reload()
      toast.success('Two-factor authentication is on')
    } catch {
      setError('That code is wrong or expired. Enter the current code from your app.')
    } finally { setBusy(false) }
  }

  const regenerate = async () => {
    setBusy(true)
    try { setRecoveryCodes(await issueCodes()); toast.success('New recovery codes generated') }
    catch (e) { toast.error(e instanceof Error ? e.message : 'Could not generate codes') }
    finally { setBusy(false) }
  }

  const turnOff = async () => {
    if (!factorId) return
    if (!confirm('Turn off two-factor authentication? Your account will be protected by password only.')) return
    setBusy(true)
    try {
      await disableFactor(factorId)
      await logEvent('mfa_disabled')
      await refreshAal()
      toast.success('Two-factor authentication turned off')
      await reload()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not turn it off. You may need to sign in again first.')
    } finally { setBusy(false) }
  }

  return (
    <Page>
      <PageHeader title="Account security" subtitle="Protect your login with two-factor authentication." />

      <Card className="mt-5 p-6">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-none items-center justify-center rounded-[12px] bg-canvas-subtle text-token-secondary"><KeyRound className="h-5 w-5" /></span>
          <div className="min-w-0 flex-1">
            <p className="text-[15px] font-bold text-token-primary">Password</p>
            <p className="mt-0.5 text-[13px] text-token-secondary">
              Set a new password for signing in here and in the MySerbisyo app on your phone.
            </p>
            <div className="mt-4 grid max-w-md grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="New password">
                <Input type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" />
              </Field>
              <Field label="Confirm password">
                <Input type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} />
              </Field>
            </div>
            <button className={btnPrimary + ' mt-4'} onClick={changePassword} disabled={pwBusy || !pw || !pw2}>
              {pwBusy ? 'Saving…' : 'Update password'}
            </button>
          </div>
        </div>
      </Card>

      {/* Recovery codes panel — shown once after enroll or regenerate */}
      {recoveryCodes && (
        <Card className="mt-5 border-token-warning p-6">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 flex-none items-center justify-center rounded-[12px] bg-token-selected text-token-success"><KeyRound className="h-5 w-5" /></span>
            <div className="min-w-0">
              <p className="text-[15px] font-bold text-token-primary">Save your recovery codes</p>
              <p className="mt-0.5 text-[13px] text-token-secondary">Store these somewhere safe. Each one lets you sign in <b>once</b> if you lose your authenticator. This is the only time they're shown.</p>
              <div className="mt-3 grid grid-cols-2 gap-x-8 gap-y-1.5 rounded-[12px] bg-token-surface p-4 font-mono text-[13.5px] text-token-primary sm:max-w-md">
                {recoveryCodes.map((c) => <span key={c}>{c}</span>)}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button className={btnSecondary} onClick={() => { navigator.clipboard?.writeText(recoveryCodes.join('\n')); toast.success('Copied') }}>Copy</button>
                <button className={btnPrimary} onClick={() => setRecoveryCodes(null)}>I've saved these</button>
              </div>
            </div>
          </div>
        </Card>
      )}

      <Card className="mt-5 p-6">
        {loading ? (
          <p className="text-[14px] text-token-secondary">Loading…</p>
        ) : factorId ? (
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 flex-none items-center justify-center rounded-[12px] bg-token-success-surface text-token-success"><ShieldCheck className="h-5 w-5" /></span>
              <div>
                <div className="flex items-center gap-2"><p className="text-[15px] font-bold text-token-primary">Two-factor authentication</p><StatusPill status="active" label="On" /></div>
                <p className="mt-0.5 text-[13px] text-token-secondary">You enter a 6-digit code from your authenticator app when you sign in.</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button className={btnGhost} onClick={regenerate} disabled={busy}>Regenerate recovery codes</button>
              <button className={btnGhost} onClick={turnOff} disabled={busy}>Turn off</button>
            </div>
          </div>
        ) : enroll ? (
          <div className="max-w-md">
            <p className="text-[15px] font-bold text-token-primary">Scan this with your authenticator app</p>
            <p className="mt-1 text-[13px] text-token-secondary">Use Google Authenticator, Authy, 1Password or similar, then enter the 6-digit code it shows.</p>
            <div className="mt-4 inline-flex rounded-[14px] border border-token-subtle bg-token-surface p-3">
              {enroll.qrSvg.startsWith('data:')
                ? <img src={enroll.qrSvg} alt="Two-factor QR code" width={180} height={180} />
                : <div className="[&>svg]:h-[180px] [&>svg]:w-[180px]" dangerouslySetInnerHTML={{ __html: enroll.qrSvg }} />}
            </div>
            <p className="mt-3 text-[12.5px] text-token-secondary">Can't scan? Enter this key manually: <code className="rounded bg-canvas-subtle px-1.5 py-0.5 text-[12px] font-semibold text-token-primary">{enroll.secret}</code></p>
            <div className="mt-4 max-w-[200px]"><Field label="6-digit code"><Input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" autoFocus /></Field></div>
            {error && <p className="mt-2 text-[13px] text-rose-600">{error}</p>}
            <div className="mt-4 flex gap-2">
              <button className={btnPrimary} onClick={confirmEnroll} disabled={busy || code.length !== 6}>{busy ? 'Verifying…' : 'Verify & turn on'}</button>
              <button className={btnGhost} onClick={() => { setEnroll(null); setCode(''); setError('') }} disabled={busy}>Cancel</button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 flex-none items-center justify-center rounded-[12px] bg-canvas-subtle text-token-secondary"><Shield className="h-5 w-5" /></span>
              <div>
                <div className="flex items-center gap-2"><p className="text-[15px] font-bold text-token-primary">Two-factor authentication</p><StatusPill status="inactive" label="Off" /></div>
                <p className="mt-0.5 max-w-md text-[13px] text-token-secondary">Add a second step to your login using an authenticator app. Optional, but strongly recommended for admins.</p>
              </div>
            </div>
            <button className={btnPrimary} onClick={startEnroll} disabled={busy}>{busy ? 'Starting…' : 'Set up'}</button>
          </div>
        )}
      </Card>

      {aal === 'aal2' && !recoveryCodes && (
        <p className="mt-3 text-[12px] text-token-secondary">This session has passed two-factor authentication.</p>
      )}

      <Card className="mt-5 p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-[15px] font-bold text-token-primary">Display</p>
            <p className="mt-0.5 max-w-xl text-[13px] text-token-secondary">Choose a fixed appearance or follow your device. This preference stays in this browser.</p>
          </div>
          <ThemeModeSelect className="w-full rounded-[10px] border border-token bg-token-surface px-3 py-2 sm:w-[220px]" />
        </div>
      </Card>

      <LanguageCard />
      <CalendarFeedCard />
      <ConnectedAppsCard />
    </Page>
  )
}

type McpConnection = {
  id: string
  client: { client_id: string; name: string }
  scopes: string[]
  status: 'active' | 'revoked' | 'expired'
  consented_at: string
  expires_at: string | null
}

/** Spec 001. The choice is saved on the account, so it follows the person to every device. */
function LanguageCard() {
  const { t } = useT()
  return (
    <Card className="mt-5 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-[15px] font-bold text-token-primary">{t('account.languageTitle')}</p>
          <p className="mt-0.5 max-w-xl text-[13px] text-token-secondary">{t('account.languageBody')}</p>
        </div>
        <LanguageMenu />
      </div>
    </Card>
  )
}

function ConnectedAppsCard() {
  const [connections, setConnections] = useState<McpConnection[]>([])
  const [loading, setLoading] = useState(true)
  const [revoking, setRevoking] = useState<string | null>(null)

  const load = async () => {
    setLoading(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) return setConnections([])
      const data = await callMcpOAuth(session.access_token, '/oauth/connections') as { connections?: McpConnection[] }
      setConnections(data.connections ?? [])
    } catch (cause) {
      toast.error(cause instanceof Error ? cause.message : 'Could not load connected apps.')
    } finally { setLoading(false) }
  }

  useEffect(() => { void load() }, [])

  const revoke = async (connection: McpConnection) => {
    if (!confirm(`Remove ${connection.client.name}? It will immediately lose access to your MySerbisyo data.`)) return
    setRevoking(connection.id)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) throw new Error('Sign in again to manage connected apps.')
      await callMcpOAuth(session.access_token, `/oauth/connections/${connection.id}/revoke`, { method: 'POST' })
      toast.success(`${connection.client.name} disconnected`)
      await load()
    } catch (cause) {
      toast.error(cause instanceof Error ? cause.message : 'Could not disconnect this app.')
    } finally { setRevoking(null) }
  }

  return (
    <Card className="mt-5 p-6">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 flex-none items-center justify-center rounded-[12px] bg-canvas-subtle text-token-secondary"><Link2 className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-bold text-token-primary">Connected apps</p>
          <p className="mt-0.5 text-[13px] text-token-secondary">Apps you&apos;ve allowed to access MySerbisyo through a secure connection.</p>
          {loading ? <p className="mt-4 text-[13px] text-token-secondary">Loading connected apps…</p>
            : connections.length === 0 ? <p className="mt-4 rounded-[10px] bg-token-surface px-3 py-2.5 text-[13px] text-token-secondary">No apps are connected.</p>
              : <div className="mt-4 divide-y divide-token-subtle rounded-[12px] border border-token-subtle">
                {connections.map((connection) => <div key={connection.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3.5">
                  <div className="min-w-0">
                    <p className="text-[13.5px] font-bold text-token-primary">{connection.client.name}</p>
                    <p className="mt-0.5 text-[12.5px] text-token-secondary">{connection.scopes.map(scopeLabel).join(' · ')} · Connected {phFormat(new Date(connection.consented_at), 'date')}</p>
                  </div>
                  {connection.status === 'active'
                    ? <button className={btnGhost} onClick={() => void revoke(connection)} disabled={revoking === connection.id}>{revoking === connection.id ? 'Disconnecting…' : <><Unplug className="mr-1.5 inline h-3.5 w-3.5" />Disconnect</>}</button>
                    : <StatusPill status="inactive" label={connection.status === 'revoked' ? 'Disconnected' : 'Expired'} />}
                </div>)}
              </div>}
        </div>
      </div>
    </Card>
  )
}

async function callMcpOAuth(accessToken: string, path: string, init: RequestInit = {}): Promise<unknown> {
  const base = import.meta.env.VITE_SUPABASE_URL as string
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY as string
  const response = await fetch(`${base}/functions/v1/mcp-oauth${path}`, { ...init, headers: { apikey: key, Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json', ...(init.headers ?? {}) } })
  const body = await response.json().catch(() => ({})) as { error_description?: string }
  if (!response.ok) throw new Error(body.error_description ?? 'Could not update this connection.')
  return body
}

function scopeLabel(scope: string) { return scope.replace(':', ' — ').replace(/\b\w/g, (letter) => letter.toUpperCase()) }

/** Spec 022 — the technician's own job calendar as a subscribable feed. Lives on the security page
 *  on purpose: the URL is a bearer credential, and this is where a user comes to revoke things. */
function CalendarFeedCard() {
  const [token, setToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)

  const url = token
    ? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/calendar-feed?token=${token}`
    : ''

  useEffect(() => {
    ;(async () => {
      const { data } = await supabase.auth.getUser()
      if (!data.user) { setLoading(false); return }
      const { data: row } = await supabase.from('users').select('calendar_token').eq('id', data.user.id).maybeSingle()
      setToken((row as { calendar_token: string | null } | null)?.calendar_token ?? null)
      setLoading(false)
    })()
  }, [])

  const rotate = async () => {
    setBusy(true)
    const { data, error } = await supabase.rpc('rotate_calendar_token')
    setBusy(false)
    if (error) return toast.error(error.message)
    setToken(data as string)
    toast.success(token ? 'New link created — the old one no longer works' : 'Calendar link created')
  }

  const disable = async () => {
    setBusy(true)
    const { error } = await supabase.rpc('disable_calendar_token')
    setBusy(false)
    if (error) return toast.error(error.message)
    setToken(null)
    toast.success('Calendar link turned off')
  }

  return (
    <Card className="mt-5">
      <div className="p-5">
        <div className="flex items-start gap-3">
          <span className="flex h-9 w-9 flex-none items-center justify-center rounded-[10px] bg-canvas-subtle text-token-secondary">
            <CalendarDays className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[14px] font-semibold text-token-primary">Your job calendar</p>
            <p className="mt-0.5 text-[12.5px] text-token-secondary">
              Subscribe in Google Calendar, Apple Calendar or Outlook to see your jobs alongside everything else.
              Read-only, and only the jobs assigned to you.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="mt-4 h-9 animate-pulse rounded-[10px] bg-canvas-subtle" />
        ) : token ? (
          <div className="mt-4 space-y-3">
            <div className="flex items-center gap-2">
              <input readOnly value={url} className="min-w-0 flex-1 rounded-[10px] border border-token-subtle bg-token-surface px-3 py-2 text-[12px] text-token-secondary" />
              <button className={btnSecondary} onClick={() => { navigator.clipboard.writeText(url); toast.success('Link copied') }}>Copy</button>
            </div>
            {/* Said plainly, because a feed URL is a password that looks like a link. */}
            <p className="text-[12px] text-token-secondary">
              Anyone with this link can see your job schedule, so treat it like a password. Calendars refresh on
              their own schedule — usually every few hours, not instantly.
            </p>
            <div className="flex gap-2">
              <button className={btnSecondary} onClick={rotate} disabled={busy}>Create a new link</button>
              <button className={btnGhost} onClick={disable} disabled={busy}>Turn off</button>
            </div>
          </div>
        ) : (
          <button className={btnPrimary + ' mt-4'} onClick={rotate} disabled={busy}>
            {busy ? 'Creating…' : 'Create calendar link'}
          </button>
        )}
      </div>
    </Card>
  )
}
